/**
 * Phase 3D: Note Analysis Hook
 * 
 * Runs rule-based detection on operative notes and stores
 * evidence snippets per CPT.
 */

import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  analyzeNote,
  getRequirementsForCpts,
  CptDetectionResult,
  NoteAnalysisResult,
  CptRequirement,
} from '@/lib/reconciliation/detectionEngine';
import { DocumentationStatusV2, EvidenceSnippet } from '@/lib/phase3/types';
import { Json } from '@/integrations/supabase/types';

interface UseNoteAnalysisOptions {
  authorizationId: string | null;
  authorizedCpts: string[];
  isDemoMode?: boolean;
}

interface AnalysisState {
  isAnalyzing: boolean;
  hasAnalyzed: boolean;
  result: NoteAnalysisResult | null;
  error: Error | null;
}

export function useNoteAnalysis({
  authorizationId,
  authorizedCpts,
  isDemoMode = false,
}: UseNoteAnalysisOptions) {
  const [state, setState] = useState<AnalysisState>({
    isAnalyzing: false,
    hasAnalyzed: false,
    result: null,
    error: null,
  });

  /**
   * Fetch database-defined documentation requirements
   */
  const fetchDbRequirements = useCallback(async (): Promise<CptRequirement[]> => {
    if (authorizedCpts.length === 0) return [];
    
    try {
      const { data, error } = await supabase
        .from('cpt_documentation_requirements')
        .select('*')
        .in('cpt_code', authorizedCpts)
        .eq('is_active', true);
      
      if (error) throw error;
      
      // Map database rows to CptRequirement format
      return (data || []).map(row => ({
        cptCode: row.cpt_code,
        requiredElements: (row.required_elements || []).map((el: string, idx: number) => ({
          id: `${row.cpt_code}_${idx}`,
          label: el,
          description: el,
          keywords: el.toLowerCase().split(/\s+/).filter(w => w.length > 3),
          strictness: 'required' as const,
        })),
      }));
    } catch (err) {
      console.error('Failed to fetch documentation requirements:', err);
      return [];
    }
  }, [authorizedCpts]);

  /**
   * Run analysis on the operative note
   */
  const runAnalysis = useCallback(async (
    noteText: string
  ): Promise<NoteAnalysisResult | null> => {
    if (!noteText || authorizedCpts.length === 0) {
      return null;
    }

    setState(prev => ({ ...prev, isAnalyzing: true, error: null }));

    try {
      // Fetch database requirements
      const dbRequirements = await fetchDbRequirements();
      
      // Get requirements for all authorized CPTs (with fallback to demo data)
      const requirements = getRequirementsForCpts(authorizedCpts, dbRequirements);
      
      // Run the analysis
      const result = analyzeNote(noteText, requirements);
      
      setState({
        isAnalyzing: false,
        hasAnalyzed: true,
        result,
        error: null,
      });
      
      return result;
    } catch (err) {
      console.error('Note analysis failed:', err);
      setState(prev => ({
        ...prev,
        isAnalyzing: false,
        error: err as Error,
      }));
      return null;
    }
  }, [authorizedCpts, fetchDbRequirements]);

  /**
   * Store analysis results in the database
   */
  const storeAnalysisResults = useCallback(async (
    analysisResult: NoteAnalysisResult
  ): Promise<boolean> => {
    if (!authorizationId) return false;

    try {
      const { data: { user } } = await supabase.auth.getUser();

      // Update each CPT reconciliation entry with evidence
      for (const cptResult of analysisResult.cptResults) {
        // Convert evidence snippets to JSON-compatible format
        const evidenceSnippetsJson: Json = cptResult.allEvidenceSnippets.map(s => ({
          text: s.text,
          offset_start: s.offset_start,
          offset_end: s.offset_end,
          matched_keywords: s.matched_keywords,
        }));

        const { error: updateError } = await supabase
          .from('cpt_reconciliation_entries')
          .update({
            evidence_snippets: evidenceSnippetsJson,
            documentation_status_v2: cptResult.documentationStatus,
            documentation_found: cptResult.detectedInNote,
            documentation_confidence: cptResult.documentationStatus === 'sufficient' 
              ? 'explicit' 
              : cptResult.documentationStatus === 'ambiguous'
                ? 'partial'
                : 'missing',
            is_demo_entry: isDemoMode,
            updated_at: new Date().toISOString(),
          })
          .eq('authorization_id', authorizationId)
          .eq('cpt_code', cptResult.cptCode);

        if (updateError) {
          console.error(`Failed to update CPT ${cptResult.cptCode}:`, updateError);
        }
      }

      // Log activity
      if (user) {
        await supabase.from('authorization_activity_log').insert({
          authorization_id: authorizationId,
          action: 'note_analyzed',
          actor_id: user.id,
          actor_type: isDemoMode ? 'system' : 'link_ops',
          details: {
            total_cpts_analyzed: analysisResult.totalCptsAnalyzed,
            cpts_with_evidence: analysisResult.cptsWithEvidence,
            cpts_with_sufficient_docs: analysisResult.cptsWithSufficientDocs,
            is_demo: isDemoMode,
          },
        });
      }

      return true;
    } catch (err) {
      console.error('Failed to store analysis results:', err);
      setState(prev => ({ ...prev, error: err as Error }));
      return false;
    }
  }, [authorizationId, isDemoMode]);

  /**
   * Run analysis and store results in one operation
   */
  const analyzeAndStore = useCallback(async (
    noteText: string
  ): Promise<{ success: boolean; result: NoteAnalysisResult | null }> => {
    const result = await runAnalysis(noteText);
    
    if (!result) {
      return { success: false, result: null };
    }

    const stored = await storeAnalysisResults(result);
    
    return { success: stored, result };
  }, [runAnalysis, storeAnalysisResults]);

  /**
   * Get analysis result for a specific CPT
   */
  const getCptResult = useCallback((cptCode: string): CptDetectionResult | undefined => {
    return state.result?.cptResults.find(r => r.cptCode === cptCode);
  }, [state.result]);

  /**
   * Reset analysis state
   */
  const resetAnalysis = useCallback(() => {
    setState({
      isAnalyzing: false,
      hasAnalyzed: false,
      result: null,
      error: null,
    });
  }, []);

  return {
    // State
    isAnalyzing: state.isAnalyzing,
    hasAnalyzed: state.hasAnalyzed,
    analysisResult: state.result,
    error: state.error,
    
    // Actions
    runAnalysis,
    storeAnalysisResults,
    analyzeAndStore,
    getCptResult,
    resetAnalysis,
  };
}
